<template>
  <div id="rg-object-history-player" v-if="showRgHistoryPlayer">
    <to-live-map-button></to-live-map-button>
    <div class="map-container">
      <div id="map" class="rg-object-history-map"></div>
      <div v-if="isLoading" class="loading-overlay">
        <div class="loader"></div>
      </div>
    </div>
    <rg-playback-controller
      v-if="!isActiveDateInputDialog"
      :nowTime="nowTime"
      :dateRange="dateRange"
      :playbackStatus="playbackStatus"
      @play="handlePlay"
      @pause="handlePause"
      @replay="handleReplay"
      @onSeekDragStart="handleSeekStart"
      @onSeekDragEnd="handleSeekEnd"
      @onPlaySpeedChange="handlePlaySpeed"
      @onLayerVisibilityChange="handleLayerVisibility"
      @openDateDialog="handleOpenDateDialog"
    ></rg-playback-controller>
    <rg-date-input-dialog
      :active="isActiveDateInputDialog"
      :displayAreas="displayAreas"
      @sendInputValue="onDateInputConfirmed"
      @onCancel="onDateInputCancel"
    />
    <rg-detail-panel
      v-for="panel in selectedPanels"
      v-show="isPanelVisible"
      :key="panel.objectId"
      :feature="panel.feature"
      :nowTime="nowTime"
      :style="{
        top: `${panel.screenY + 22}px`,
        left: `${panel.screenX - 20}px`,
      }"
      @close="handleClosePanel"
    />
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import { useMapbox } from "./composables/useMapbox";
import { usePlaybackManager } from "./composables/usePlaybackManager";
import C from "./Constants";
import RadarGridService from "./services/radarGridService";
import LocalDbService from "@/services/localDbService";
import { LocalizeMixin, DeviceInfoMixin } from "@/mixins";
import ToLiveMapButton from "./components/ToLiveMapButton.vue";
import RgPlaybackController from "./components/RgPlaybackController/RgPlaybackController.vue";
import RgDateInputDialog from "./components/RgDateInputDialog.vue";
import RgDetailPanel from "./components/RgDetailPanel.vue";
import Vue from "vue";
import * as turf from "@turf/turf";

export default {
  name: "RgObjectHistoryPlayer",
  mixins: [LocalizeMixin, DeviceInfoMixin],
  components: {
    ToLiveMapButton,
    RgPlaybackController,
    RgDateInputDialog,
    RgDetailPanel,
  },

  data() {
    return {
      mapStyle: "",
      pos: null,
      displayAreas: [],
      selectedPanels: [],
      isPanelVisible: true,
      myVesselIds: [],
      isActiveDateInputDialog: false,
      isInitialDateInputDialog: true,
      playbackManager: null,
      mapbox: null,
      map: null,
      nowTime: 0,
      playbackStatus: "pause",
      dateRange: null,
      filledPointsById: {},
      availableEndTime: 0,
      isLoading: false,
    };
  },

  computed: {
    showRgHistoryPlayer() {
      return true;
    },
    mapSetting() {
      return {
        container: "rg-object-history-player-map",
        style: this.mapStyle,
        center: C.PLAYBACK_AREA_CENTER,
        zoom: 10,
        doubleClickZoom: false,
        maxZoom: 18,
        attributionControl: false,
        localIdeographFontFamily: "'Noto Sans', sans-serif",
      };
    },
  },

  methods: {
    async onDateInputConfirmed({ datetime, displayArea }) {
      this.isInitialDateInputDialog = false;
      this.isActiveDateInputDialog = false;

      this.isLoading = true;

      try {
        // リセット
        if (this.playbackManager) {
          this.playbackManager = null;
        }
        this.selectedPanels = [];
        this.mapbox.setSourceData(C.SOURCE_ID.RG_HISTORY_SOURCE, {
          type: "FeatureCollection",
          features: [],
        });

        this.mapbox.setSourceData(C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA, {
          type: "FeatureCollection",
          features: [],
        });

        const swLat = displayArea.south_west_latitude;
        const swLng = displayArea.south_west_longitude;
        const neLat = displayArea.north_east_latitude;
        const neLng = displayArea.north_east_longitude;

        // 四隅の座標計算
        const southWest = [swLng, swLat];
        const northWest = [swLng, neLat];
        const northEast = [neLng, neLat];
        const southEast = [neLng, swLat];

        // ポリゴン座標（最初の点を最後に繰り返して閉じる）
        const polygonCoordinates = [
          [southWest, northWest, northEast, southEast, southWest],
        ];

        // 表示エリアのポリゴンを作成
        const displayAreaPolygon = turf.polygon(polygonCoordinates);

        this.mapbox.setSourceData(
          C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA,
          displayAreaPolygon
        );

        // API取得（レーダー & AIS）
        const BUFFER_SECONDS = 60; // 1分前から取得するとする
        const startUnixOriginal = Math.floor(datetime.getTime() / 1000);
        const startUnix = startUnixOriginal - BUFFER_SECONDS;
        const endUnix = startUnixOriginal + 3 * 60 * 60;

        this.dateRange = { startTime: startUnixOriginal, endTime: endUnix };
        this.nowTime = startUnixOriginal;

        const radarGridHistories = await RadarGridService.fetchRadarGridHistory(
          displayArea,
          startUnix,
          endUnix
        );

        const aisHistories = await RadarGridService.fetchAisVesselsHistory(
          displayArea,
          startUnix,
          endUnix
        );

        // 再生用データに正規化（Feature）
        const normalizeToPlaybackObjects = (data, sourceType) =>
          Object.entries(data).map(([objectId, obj]) => {
            const base = {
              objectId,
              source: sourceType,
              points: obj.points,
            };

            if (sourceType === "ais") {
              return {
                ...base,
                properties: {
                  id: obj.id,
                  isMyVessel: this.myVesselIds.includes(obj.id),
                  name: obj.n,
                  nameJp: obj.n_j || "",
                  mmsi: obj.mmsi,
                  callSign: obj.cs,
                  cc: obj.cc,
                  category: obj.cat,
                },
              };
            } else {
              return {
                ...base,
                properties: {
                  id: objectId,
                  name: obj.n,
                },
              };
            }
          });

        const radarPlayback = normalizeToPlaybackObjects(
          radarGridHistories,
          "radar"
        );
        const aisPlayback = normalizeToPlaybackObjects(aisHistories, "ais");
        const allPlaybackObjects = [...radarPlayback, ...aisPlayback];

        // 再生マネージャー初期化（Mapboxの updateFeature を使って描画）
        this.playbackManager = usePlaybackManager(
          allPlaybackObjects,
          this.dateRange.startTime,
          this.mapbox
        );

        this.playbackManager.onTick(t => {
          this.nowTime = t;
          this.updatePanelPositions();
          // 全てのパネルの位置を更新
          this.selectedPanels = this.selectedPanels.map(panel => {
            const feature = this.playbackManager.getLatestFeature(
              panel.objectId
            );

            if (feature && feature.geometry) {
              const [lon, lat] = feature.geometry.coordinates;
              const { x, y } = this.map.project([lon, lat]);

              return {
                ...panel,
                feature,
                screenX: x,
                screenY: y,
              };
            } else {
              // featureが見つからない場合は削除対象
              return null;
            }
          });
        });

        // 履歴再生の中心へ移動
        const bounds = new mapboxgl.LngLatBounds(
          [displayArea.south_west_longitude, displayArea.south_west_latitude],
          [displayArea.north_east_longitude, displayArea.north_east_latitude]
        );

        const cameraOptions = this.map.cameraForBounds(bounds, {
          padding: 20,
        });

        this.mapbox.jumpTo(cameraOptions.center, cameraOptions.zoom - 1);

        this.handleSeekEnd(startUnixOriginal);
      } catch (e) {
        Vue.$log.error("履歴データ取得エラー", e);
        this.$vs.notify({
          title: "履歴データの取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    onDateInputCancel() {
      if (this.isInitialDateInputDialog) {
        this.$router.push({ name: "enterprise-live-map" });
      } else {
        this.isActiveDateInputDialog = false;
      }
    },

    updatePanelPositions() {
      const nowSec = this.nowTime;
      this.selectedPanels = this.selectedPanels
        .map(panel => {
          const feature = this.playbackManager.getLatestFeature(panel.objectId);

          if (
            feature &&
            feature.geometry &&
            feature.properties &&
            nowSec - feature.properties.t <= 60
          ) {
            const [lon, lat] = feature.geometry.coordinates;
            const { x, y } = this.map.project([lon, lat]);
            return { ...panel, feature, screenX: x, screenY: y };
          } else {
            this.handleClosePanel(panel.objectId);
            return null;
          }
        })
        .filter(panel => panel !== null);
    },

    async handlePlay() {
      this.playbackStatus = "play";
      this.playbackManager.play();
    },

    handlePause() {
      this.playbackStatus = "pause";
      this.playbackManager.pause();
    },

    handleReplay() {
      this.playbackStatus = "replay";
      this.playbackManager.seek(this.dateRange.startTime);
    },

    handleSeekStart() {
      this.playbackStatus = "pause";
      this.playbackManager.stopAnimationOnly();
    },

    handleSeekEnd(time) {
      const resumed = this.playbackManager.seek(time);
      if (resumed) {
        this.playbackStatus = "play";
      }
    },

    handlePlaySpeed(speed) {
      this.playbackManager.setSpeed(speed);
    },

    handleOpenDateDialog() {
      if (this.playbackStatus === "play") {
        this.playbackManager.pause();
        this.playbackStatus = "pause";
      }
      this.isActiveDateInputDialog = true;
    },

    handleLayerVisibility(val) {
      this.mapbox.setLayerVisibility(val.layerId, val.isVisible);
      if (val.layerId === C.LAYER_ID.RG_OBJECT_LAYER) {
        this.mapbox.setLayerVisibility(
          C.LAYER_ID.RG_OBJECT_FLAME_LAYER,
          val.isVisible
        );
      }
    },

    handleClosePanel(objectId) {
      this.selectedPanels = this.selectedPanels.filter(
        p => p.objectId !== objectId
      );
    },

    handleZoomChange() {
      const zoom = this.map.getZoom();
      this.isPanelVisible = zoom >= 10;
    },
  },

  async mounted() {
    this.mapStyle = await LocalDbService.getMapStyle();
    this.pos = await LocalDbService.getDefaultPosition();

    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
    this.map = new mapboxgl.Map({
      ...this.mapSetting,
      container: "map",
    });

    this.map.on("load", async () => {
      this.mapbox = useMapbox(this.map);
      this.mapbox.addSources();
      this.mapbox.addLayers();
      this.mapbox.addClickEvents();

      this.mapbox.onRgObjectClick((feature, screenX, screenY) => {
        const objectId = feature.properties.objectId;
        // 重複チェック
        if (!this.selectedPanels.find(p => p.objectId === objectId)) {
          this.selectedPanels.push({
            objectId,
            feature,
            screenX,
            screenY,
          });
        }
      });

      // マップ動いたらパネル位置再計算
      this.mapbox.onMapMove(() => {
        this.updatePanelPositions();
      });

      this.mapbox.onZoomChange(zoom => {
        this.handleZoomChange(zoom);
      });

      this.map.on("zoom", this.handleZoomChange);

      try {
        this.displayAreas = await RadarGridService.fetchDisplayAreas();
      } catch (e) {
        Vue.$log.error("表示エリア取得エラー", e);
        this.$vs.notify({
          title: "表示エリアの取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      }

      this.myVesselIds = await RadarGridService.fetchMyVesselIds();

      // ダイアログ表示
      this.isActiveDateInputDialog = true;
    });
  },
};
</script>

<style lang="scss" scoped>
.router-view {
  &:has(#rg-object-history-player) {
    padding: 0rem !important;
  }
}

.rg-object-history-map {
  width: 100vw;
  min-height: 100vh; /* Fallback */
  min-height: calc(var(--vh, 1vh) * 100);

  position: relative;
  ::v-deep .mapboxgl-ctrl-logo {
    display: none !important;
  }
  ::v-deep .rg-wake-line-popup-wrapper {
    z-index: 11;
  }
}

.map-container {
  position: relative;
  width: 100vw;
  height: 100vh;
}

.rg-object-history-map {
  width: 100%;
  height: 100%;
}

.loading-overlay {
  position: fixed; /* ← relativeではなく fixed！画面全体に張る */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(2, 20, 49, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader {
  border: 6px solid #b0b0b0;
  border-top: 6px solid #f3f3f3;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
